import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Heading,
  Link,
  Paragraph,
  Skeleton,
  Tag,
} from '@digdir/designsystemet-react';
import type { MeasureStatus, TeamPage } from '../../api/workEnvironmentTypes';
import { useTeamPageQuery } from '../../hooks/queries/useTeamPageQuery';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { longDate } from '../Settings/privacyView';
import { monthAndYear } from '../WorkEnvironmentSurveyDetail/evaluationView';
import { LEVEL_COLORS } from '../WorkEnvironmentSurveyDetail/riskView';
import { dayAndMonth } from '../WorkEnvironmentSurveyDetail/surveyProcessView';
import { teamLink } from '../WorkEnvironmentSurveyDetail/measuresView';
import { TrustpulseLogo } from '../../components/TrustpulseLogo';
import form from '../FormSubmission/WorkEnvironmentFormSubmission.module.css';
import teamMeasures from '../WorkEnvironmentTeamMeasures/WorkEnvironmentTeamMeasures.module.css';
import classes from './WorkEnvironmentTeamPage.module.css';
import {
  decisionText,
  measuresDone,
  shieldedNames,
  shieldedTexts,
  stepDetail,
  whereWeAre,
} from './teamPageView';

const STATUS_COLORS: Record<MeasureStatus, 'neutral' | 'info' | 'success'> = {
  NOT_STARTED: 'neutral',
  IN_PROGRESS: 'info',
  DONE: 'success',
};

const isNotFound = (error: unknown) =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  404;

const Steps = ({ data }: { data: TeamPage }): ReactElement => {
  const { t, i18n } = useTranslation();
  const title = (step: TeamPage['steps'][number]['step']) =>
    step === 'assessed' && data.assessment?.withSafetyRep
      ? t('workEnvironmentTeamPage.steps.assessedWithRep')
      : t(`workEnvironmentTeamPage.steps.${step}`);
  return (
    <section aria-labelledby="team-where-heading" className={classes.card}>
      <div className={classes.heading}>
        <Heading level={2} data-size="sm" id="team-where-heading">
          {t('workEnvironmentTeamPage.whereTitle')}
        </Heading>
        <Paragraph data-size="sm" className={classes.subtle}>
          {whereWeAre(data, t, i18n.language)}
        </Paragraph>
      </div>
      <ol className={classes.steps}>
        {data.steps.map(({ step, state }) => {
          const detail =
            state === 'current'
              ? t('workEnvironmentTeamPage.steps.current')
              : state === 'done' || step === 'evaluation'
                ? stepDetail(step, data, t, i18n.language)
                : null;
          return (
            <li
              key={step}
              className={classes.step}
              data-state={state}
              aria-current={state === 'current' ? 'step' : undefined}
            >
              <span aria-hidden className={classes.dot} />
              <span className={classes.stepText}>
                <span
                  className={`ds-paragraph ${classes.stepTitle}`}
                  data-size="sm"
                >
                  {title(step)}
                  <span className="ds-sr-only">
                    {' – '}
                    {t(
                      `workEnvironmentTeamPage.steps.${state === 'done' ? 'done' : state === 'current' ? 'currentLabel' : 'upcoming'}`,
                    )}
                  </span>
                </span>
                {detail && (
                  <span
                    className={`ds-paragraph ${classes.subtle}`}
                    data-size="xs"
                  >
                    {detail}
                  </span>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

const Plan = ({ plan }: { plan: TeamPage['plans'][number] }): ReactElement => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby={`team-plan-${plan.key}`} className={classes.card}>
      <div className={classes.heading}>
        {plan.level && (
          <div>
            <Tag data-color={LEVEL_COLORS[plan.level]} data-size="sm">
              {t(`workEnvironmentRisk.matrix.levels.${plan.level}`)}
            </Tag>
          </div>
        )}
        <Heading level={2} data-size="sm" id={`team-plan-${plan.key}`}>
          {t('workEnvironmentTeamPage.plan.title', { name: plan.name })}
        </Heading>
      </div>
      {plan.goal && (
        <div className={classes.goal}>
          <span className="ds-label" data-size="sm">
            {t('workEnvironmentTeamPage.plan.goal')}
          </span>
          <Paragraph data-size="sm">{plan.goal}</Paragraph>
        </div>
      )}
      <Heading level={3} data-size="xs">
        {t('workEnvironmentTeamPage.plan.measures', {
          done: measuresDone(plan),
          count: plan.measures.length,
        })}
      </Heading>
      <ul className={classes.rows}>
        {plan.measures.map((measure) => (
          <li key={measure.title} className={classes.row}>
            <span className={classes.rowText}>
              <span className="ds-label" data-size="sm">
                {measure.title}
              </span>
              <span className={`ds-paragraph ${classes.subtle}`} data-size="xs">
                {t('workEnvironmentTeamPage.plan.meta', {
                  owner: t(
                    `workEnvironmentTeamPage.plan.owners.${measure.owner ?? 'none'}`,
                  ),
                  date: dayAndMonth(measure.dueDate, i18n.language),
                })}
              </span>
            </span>
            <Tag data-color={STATUS_COLORS[measure.status]} data-size="sm">
              {t(`workEnvironmentTeamPage.plan.statuses.${measure.status}`)}
            </Tag>
          </li>
        ))}
      </ul>
      <dl className={classes.facts}>
        {plan.nextReview && (
          <div>
            <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {t('workEnvironmentTeamPage.plan.nextReview')}
            </dt>
            <dd className="ds-label" data-size="sm">
              {t('workEnvironmentTeamPage.plan.nextReviewValue', {
                date: longDate(plan.nextReview, i18n.language),
              })}
            </dd>
          </div>
        )}
        {plan.evaluationDate && (
          <div>
            <dt className={`ds-paragraph ${classes.subtle}`} data-size="xs">
              {t('workEnvironmentTeamPage.plan.measured')}
            </dt>
            <dd className="ds-label" data-size="sm">
              {t('workEnvironmentTeamPage.plan.measuredValue', {
                date: longDate(plan.evaluationDate, i18n.language),
              })}
            </dd>
          </div>
        )}
      </dl>
      <Paragraph data-size="xs" className={classes.subtle}>
        {t(
          plan.withSafetyRep
            ? 'workEnvironmentTeamPage.plan.madeWithRep'
            : 'workEnvironmentTeamPage.plan.approved',
          {
            date: longDate(plan.approvedAt, i18n.language),
          },
        )}
      </Paragraph>
    </section>
  );
};

const Content = ({
  data,
  guid,
}: {
  data: TeamPage;
  guid: string;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const shielded = shieldedNames(data.areas, t);
  const shieldedText = shieldedTexts(data, shielded, t);
  return (
    <>
      <div className={form.intro}>
        <span className={`ds-label ${form.eyebrow}`} data-size="sm">
          {t('workEnvironmentTeamPage.eyebrow', { title: data.title })}
        </span>
        <Heading level={1} data-size="xl" className={form.title}>
          {t('workEnvironmentTeamPage.heading')}
        </Heading>
        <Paragraph className={form.introText}>
          {t('workEnvironmentTeamPage.intro', {
            responses: data.survey.responses,
            expected: data.survey.expected,
            month: monthAndYear(data.survey.from, i18n.language),
          })}
        </Paragraph>
      </div>
      <Steps data={data} />
      {data.plans.map((plan) => (
        <Plan key={plan.key} plan={plan} />
      ))}
      {data.otherAreas.length > 0 && (
        <section aria-labelledby="team-others-heading" className={classes.card}>
          <div className={classes.heading}>
            <Heading level={2} data-size="sm" id="team-others-heading">
              {t('workEnvironmentTeamPage.others.title')}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t('workEnvironmentTeamPage.others.intro')}
            </Paragraph>
          </div>
          <ul className={classes.rows}>
            {data.otherAreas.map((area) => (
              <li key={area.key} className={classes.row}>
                <span className={classes.rowText}>
                  <span className={classes.rowTitle}>
                    <span className="ds-label" data-size="sm">
                      {area.name}
                    </span>
                    <Tag data-color={LEVEL_COLORS[area.level]} data-size="sm">
                      {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                    </Tag>
                  </span>
                  <span className="ds-paragraph" data-size="sm">
                    {decisionText(area, data, t, i18n.language)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      <section aria-labelledby="team-result-heading" className={classes.card}>
        <div className={classes.heading}>
          <Heading level={2} data-size="sm" id="team-result-heading">
            {t('workEnvironmentTeamPage.result.title')}
          </Heading>
          <Paragraph data-size="sm" className={classes.subtle}>
            {t('workEnvironmentTeamPage.result.intro')}
          </Paragraph>
        </div>
        <ul className={classes.rows}>
          {data.areas.map((area) => (
            <li key={area.key} className={classes.row}>
              <span className="ds-paragraph" data-size="sm">
                {area.name}
              </span>
              {area.level ? (
                <Tag data-color={LEVEL_COLORS[area.level]} data-size="sm">
                  {t(`workEnvironmentRisk.matrix.levels.${area.level}`)}
                </Tag>
              ) : (
                <Tag data-color="neutral" data-size="sm">
                  {t('workEnvironmentTeamPage.result.shielded')}
                </Tag>
              )}
            </li>
          ))}
        </ul>
        {shielded && (
          <Alert data-color="info" data-size="sm">
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentTeamPage.result.whyTitle')}
            </Heading>
            <Paragraph data-size="sm">
              {shieldedText.why}
            </Paragraph>
            <Paragraph data-size="sm">
              {shieldedText.report}
            </Paragraph>
            {data.whistleblowingUrl && (
              <Link href={data.whistleblowingUrl} data-size="sm">
                {t('workEnvironmentTeamPage.result.reportLink')}
              </Link>
            )}
          </Alert>
        )}
      </section>
      {(data.canPropose || data.safetyRep) && (
        <section
          aria-labelledby="team-actions-heading"
          className={classes.heading}
        >
          <Heading level={2} data-size="sm" id="team-actions-heading">
            {t('workEnvironmentTeamPage.actions.title')}
          </Heading>
          <div className={classes.actions}>
            {data.canPropose && (
              <div className={classes.card}>
                <Heading level={3} data-size="xs">
                  {t('workEnvironmentTeamPage.actions.ideaTitle')}
                </Heading>
                <Paragraph data-size="sm">
                  {t('workEnvironmentTeamPage.actions.ideaText')}
                </Paragraph>
                <div>
                  <Button asChild variant="secondary" data-size="sm">
                    <a href={teamLink(window.location.origin, guid)}>
                      {t('workEnvironmentTeamPage.actions.propose')}
                    </a>
                  </Button>
                </div>
              </div>
            )}
            {data.safetyRep && (
              <div className={classes.card}>
                <Heading level={3} data-size="xs">
                  {t('workEnvironmentTeamPage.actions.repTitle')}
                </Heading>
                <Paragraph data-size="sm">
                  {t('workEnvironmentTeamPage.actions.repText')}
                </Paragraph>
                <div>
                  <Button asChild variant="secondary" data-size="sm">
                    <a href={`mailto:${data.safetyRep}`}>
                      {t('workEnvironmentTeamPage.actions.email')}
                    </a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
};

/** The result and the plan for the team, through the shared link, without logging in */
export const WorkEnvironmentTeamPage = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const { guid } = useParams<{ guid: string }>();
  const { data, isLoading, error } = useTeamPageQuery(
    guid,
    questionLanguageFor(i18n.language),
  );

  return (
    <div className={form.page} data-size="md">
      <header className={form.header}>
        <div className={form.headerContent}>
          <TrustpulseLogo />
          {data && (
            <span className={`ds-paragraph ${teamMeasures.teamName}`}>
              {data.team}
            </span>
          )}
        </div>
      </header>
      <main id="main" className={form.main}>
        <div aria-hidden="true" className={form.backdrop} />
        <div className={form.content}>
          {isLoading && (
            <Skeleton variant="rectangle" width="100%" height={320} />
          )}
          {error && (
            <Alert data-color={isNotFound(error) ? 'info' : 'danger'}>
              <Paragraph>
                {t(
                  isNotFound(error)
                    ? 'workEnvironmentTeamPage.notFound'
                    : 'workEnvironmentTeamPage.loadFailed',
                )}
              </Paragraph>
            </Alert>
          )}
          {data && guid && <Content data={data} guid={guid} />}
        </div>
      </main>
    </div>
  );
};
